// --------------------------------------------------------------------------
// Purpose of this component is to convert EAN to wine information,
// ensuring looks up no more than a single EAN at a time, looks up the same
// EAN no more than once, and queues any additional scan data found in the
// meantime
// --------------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';

// --------------------------------------------------------------------------

import GenericScanner from '../generic-scanner';
import { getWineDataByBarCode } from './functions';
import cms from '../../models/cms';

// --------------------------------------------------------------------------

const stateIdle = 'idle';
const stateProcessing = 'processing';
const stateWaitingOnAPI = 'waiting';
//const stateDone = 'done'; // never done?
const statePaused = 'paused';

// --------------------------------------------------------------------------

class WineScanner extends React.Component {

    // ----------------------------------------------------------------------
    // validates wines
    // - queues up wines for API processing
    // ----------------------------------------------------------------------

    state = {
        
        // new codes

        analysisStateNumber : 0,            // change to trigger state changes
        analysisState       : stateIdle,    // state
        barcodesToTest      : '',           // list of barcodes queued for testing
        barcodeTesting      : '',           // current barcode being tested
        barcodesToIgnore    : '',           // list of barcodes not to lookup - must be empty for each new scan
        winesFound          : []            // list of wines found

    };

    // ----------------------------------------------------------------------

    queueBarCode(codes) {

        let code = codes[0].code; 

        this.setState( (state) => {
            
            let newQueue = state.barcodesToTest;
            let newBarcodeTesting = state.barcodeTesting;

            newQueue = newQueue==='' ? code : newQueue + ',' + code;
            
            return {
                barcodesToTest : newQueue,
                barcodeTesting : newBarcodeTesting
            };

        } );
    }

    // ----------------------------------------------------------------------
    
    retryInternet() {
        
        console.log('retrying internet');
        
        this.setState((state) => { return {
            analysisStateNumber     : state.analysisStateNumber + 1,
            analysisState           : stateProcessing,
        } } );

    }

    // ----------------------------------------------------------------------
    
    async triggerLookup() {
       
        let self = this;

        let barcodeToTest = this.state.barcodeTesting;
        
        if(this.state.barcodesToIgnore.indexOf(barcodeToTest)>=0) {
            
            this.props.onStatusUpdate('skipping');

            // don't bother
            this.setState((state)=>{ return {
                analysisStateNumber : state.analysisStateNumber + 1,
                analysisState       : stateIdle,
                barcodeTesting      : ''
            }; })

        } else {
            
            // Look it up
            this.setState((state)=>{ return { analysisStateNumber: state.analysisStateNumber + 1, analysisState: stateWaitingOnAPI }; })
            
            this.props.onStatusUpdate('validating', barcodeToTest);

            //const mappingDataURL = 'https://dw-bs-uat.web.app/data/ean-mapping.json';
            //const mappingDataURL = 'https://www.laithwaites.co.uk/json/barcode/barcodemap.json';

            const mappingDataURL = cms.retrieveScannerData(); // current domain on this path
            const APIURLprefix = this.props.APIURLprefix || '';                                  // current domain root

            let result = await getWineDataByBarCode(barcodeToTest, mappingDataURL, APIURLprefix, mappingDataURL?.body );

            switch(result.state) {
                
                case 'success': {

                    this.props.onStatusUpdate('success');

                    self.setState((state) => {
                        let winesFound = state.winesFound ?? [];
                        for(let i=0; i<result.wines.length; i++) {
                            winesFound.push(result.wines[i]);
                        }
                        return {
                            analysisStateNumber     : state.analysisStateNumber + 1,
                            analysisState           : stateIdle,                                    // was stateDone but now we wish to continue scanning
                            barcodeTesting          : '',                                           // clear barcodeTesting
                            winesFound              : winesFound,                                   // this supercedes barcodeFound
                            barcodesToIgnore        : state.barcodesToIgnore + ',' + barcodeToTest, // ignore it for next time, we got it already                            
                        }
                    });
                    
                    self.props.onWinesFound( result.wines );
                    break;
                }
                
                case 'noSuchProduct': {

                    self.setState((state) => { return {
                        analysisStateNumber     : state.analysisStateNumber + 1,
                        analysisState           : stateIdle,                                        // idle state
                        barcodeTesting          : '',                                               // clear barcodeTesting
                        barcodesToIgnore        : state.barcodesToIgnore + ',' + barcodeToTest      // ignore it for next time, we know it is wrong
                    }});

                    this.props.onStatusUpdate('failure');

                    break;

                }
                
                case 'badData': // how to handle this? it will always fail if the data is bad and it isn't an internet issue...
                case 'noInternet':
                default: {

                    this.props.onStatusUpdate('no internet');
                    this.queueBarCode(barcodeToTest);
                    self.setState((state) => { return {
                        analysisStateNumber     : state.analysisStateNumber + 1,
                        analysisState           : statePaused,                                      // pause
                    } } );

                    break;
                }

            }

        }

    }

    // ----------------------------------------------------------------------

    componentDidUpdate(prevProps, prevState) { // ok - this is going to do a LOT of the work co-ordinating everything

        // we were paused, so if this changes then it has been flagged that we should retry internet
        if(this.state.analysisState==statePaused && prevProps.internetFlag != this.props.internetFlag) {
            this.retryInternet();
        }

        // State changes triggered on component update
        // -------------------------------------------
        // stateIdle            ---> stateProcessing (if something in the queue - note: barcode scanner asynchronously puts things in the queue, regardless of current state)
        //
        // stateProcessing      -+-> stateWaitingOnAPI (if have to call API to look up barcode)
        //                       +-> stateIdle (if already know the answer is not a match)
        //
        // statePaused          ----> stateProcessing (if told internet back)
        //
        // State changes triggered by external response
        // --------------------------------------------
        // stateWaitingOnAPI    -+-> stateDone (if API returns a match - triggered on callback)
        //                       +-> stateIdle (if API does not match - triggered on callback)

        let action = prevState.analysisStateNumber!==this.state.analysisStateNumber ? 'changedto' : 'unchanged';
       
        switch(action + ':' + this.state.analysisState) {

            case 'changedto:'+stateProcessing:

                this.triggerLookup();
                
                break;

            case 'changedto:'+stateIdle:
            case 'unchanged:'+stateIdle:

                // not doing anything at the moment, so lets check the queue and maybe do something
                if(this.state.barcodesToTest!=null && this.state.barcodesToTest!=='') {

                    let list = this.state.barcodesToTest.split(',');
                    let newList = list.slice(1).join(',');
                    
                    this.setState( (state) => {
                        return {
                            analysisStateNumber: state.analysisStateNumber + 1,
                            analysisState: stateProcessing,
                            barcodeTesting: list[0],
                            barcodesToTest: newList
                            }
                        }
                    );
                    
                }

                break;

        }

    }

    // ----------------------------------------------------------------------

    render() {

        return (
                
            <GenericScanner
                onDetected={ (result) => this.queueBarCode(result)  }
                onError={ (err) => this.props.onError(err) } 
                useCameraId={ this.props.useCameraId }
                />
                
               
        );
    }

}

// --------------------------------------------------------------------------

WineScanner.propTypes = {
    onStop: PropTypes.func.isRequired,
    onWinesFound: PropTypes.func.isRequired,
    onStatusUpdate: PropTypes.func.isRequired
};

// --------------------------------------------------------------------------

export default WineScanner;

// --------------------------------------------------------------------------
